
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Heart, Trash2 } from 'lucide-react';

interface JobInterest {
  interest_id: string;
  job_id: string;
  title: string;
  company: string;
  url: string;
  created_at: string;
}

const JobInterestsCard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [jobInterests, setJobInterests] = useState<JobInterest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchJobInterests();
    }
  }, [user?.id]);

  const fetchJobInterests = async () => {
    setIsLoading(true);
    try {
      // First fetch job interests with their job IDs
      const { data: interestsData, error: interestsError } = await supabase
        .from('job_interests')
        .select('id, job_id, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (interestsError) throw interestsError;

      // Now fetch details for each job
      const jobDetails = [];
      for (const interest of interestsData || []) {
        const { data: jobData, error: jobError } = await supabase
          .from('job_listings')
          .select('title, company, url')
          .eq('id', interest.job_id)
          .single();

        if (jobError) {
          console.error('Error fetching job details:', jobError);
          continue;
        }

        jobDetails.push({
          interest_id: interest.id,
          job_id: interest.job_id,
          title: jobData?.title || 'Unknown Position',
          company: jobData?.company || 'Unknown Company',
          url: jobData?.url || '#',
          created_at: interest.created_at
        });
      }

      setJobInterests(jobDetails);
    } catch (error) {
      console.error('Error fetching job interests:', error.message);
      toast({
        title: "Error",
        description: "Failed to load your job interests. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveInterest = async (interestId: string) => {
    try {
      const { error } = await supabase
        .from('job_interests')
        .delete()
        .eq('id', interestId);

      if (error) throw error;

      setJobInterests(prev => prev.filter(interest => interest.interest_id !== interestId));

      toast({
        title: "Interest Removed",
        description: "Job removed from your interests.",
      });
    } catch (error) {
      console.error('Error removing job interest:', error.message);
      toast({
        title: "Error",
        description: "Failed to remove interest. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl text-gray-800">
            <Heart className="h-5 w-5 text-[#0EA5E9]" />
            My Job Interests
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-40">
            <p className="text-gray-500">Loading your job interests...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (jobInterests.length === 0) {
    return (
      <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl text-gray-800">
            <Heart className="h-5 w-5 text-[#0EA5E9]" />
            My Job Interests
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-40">
            <p className="text-gray-500">You haven't expressed interest in any jobs yet.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl text-gray-800">
          <Heart className="h-5 w-5 text-[#0EA5E9]" />
          My Job Interests
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {jobInterests.map((interest) => (
          <div key={interest.interest_id} className="p-4 bg-white border border-[#0EA5E9]/20 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium text-gray-800">{interest.title}</h3>
                <p className="text-sm text-gray-600">{interest.company}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Interested on {new Date(interest.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-[#0EA5E9] border-[#0EA5E9]/20 hover:bg-[#0EA5E9]/10"
                  onClick={() => window.open(interest.url, '_blank')}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  className="text-red-500 border-red-200 hover:bg-red-50"
                  onClick={() => handleRemoveInterest(interest.interest_id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default JobInterestsCard;
